// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Styled, Space, Icon} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {usePopover, useModal} from '@supermove/hooks';
import {RefundModel, PaymentModel} from '@supermove/models';
import {Typography, colors} from '@supermove/styles';
import {Datetime, Currency} from '@supermove/utils';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import Table from '@shared/design/components/TableV2Deprecated';
import DeleteRefundModal from 'modules/Refund/components/DeleteRefundModal';

const RefundRow = Styled.View`
  flex-direction: row;
  align-items: center;
  padding: 0px 0px 8px 12px;
  margin: 8px 0px 8px 8px;
`;

const Container = Styled.View`
  flex: 1;
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const FlexContainer = Styled.View<{flex: number}>`
  flex: ${({flex}) => flex};
`;

const WidthContainer = Styled.View<{width: number}>`
  width: ${({width}) => width};
`;

const RefundText = Styled.Text`
    ${Typography.Responsive.Body}
    color: ${colors.red.warning}
`;

const RefundActionCell = ({refund, refetch}: {refund: RefundModel; refetch: () => void}) => {
  const paymentActionsPopover = usePopover();
  const deleteRefundModal = useModal({name: 'Delete Refund Modal'});

  return (
    <Row style={{flex: 1, justifyContent: 'center'}}>
      <ActionMenuPopover
        popover={paymentActionsPopover}
        placement={ActionMenuPopover.Position.LeftStart}
        width={200}
        actions={[
          {
            text: 'Delete',
            onPress: () => {
              paymentActionsPopover.handleClose();
              deleteRefundModal.handleOpen();
            },
            color: colors.red.warning,
          },
        ]}
      >
        <TertiaryButton onPress={paymentActionsPopover.handleToggle} style={{paddingHorizontal: 8}}>
          <Icon source={Icon.EllipsisV} size={16} color={colors.gray.secondary} />
        </TertiaryButton>
      </ActionMenuPopover>
      <DeleteRefundModal
        isOpen={deleteRefundModal.isOpen}
        handleClose={deleteRefundModal.handleClose}
        refund={refund}
        refetch={refetch}
      />
    </Row>
  );
};

const RefundPaymentsItem = ({refund, refetch}: {refund: RefundModel; refetch: () => void}) => {
  return (
    <Row>
      <FlexContainer flex={1}>
        <Row>
          <Icon source={Icon.ArrowTurnDownRight} color={colors.gray.secondary} size={12} />
          <Space width={36} />
          <Table.CellText>
            {Datetime.convertToDisplayDatetime(refund.timestamp, Datetime.DISPLAY_DATETIME)}
          </Table.CellText>
        </Row>
      </FlexContainer>
      <FlexContainer flex={0.5} />
      <FlexContainer flex={2}>
        <Table.CellText numberOfLines={2}>{refund.name}</Table.CellText>
      </FlexContainer>
      <FlexContainer flex={1.5} />
      <FlexContainer flex={2}>
        <Table.CellText numberOfLines={2}>{refund.description}</Table.CellText>
      </FlexContainer>
      <FlexContainer flex={1}>
        <RefundText color={colors.red.warning}>-{Currency.display(refund.amount)}</RefundText>
      </FlexContainer>
      <WidthContainer width={80}>
        <RefundActionCell refund={refund} refetch={refetch} />
      </WidthContainer>
    </Row>
  );
};

const RefundPayments = ({item, refetch}: {item: PaymentModel; refetch: () => void}) => {
  const {refunds} = item;
  // If there are no refunds, don't render anything
  if (_.isEmpty(refunds)) {
    return null;
  }

  return (
    <RefundRow>
      <Container>
        {refunds.map((refund: RefundModel) => (
          <React.Fragment>
            <Space height={8} />
            <RefundPaymentsItem key={refund.id} refund={refund} refetch={refetch} />
            <Space height={8} />
          </React.Fragment>
        ))}
      </Container>
    </RefundRow>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
RefundPayments.fragment = gql`
  ${DeleteRefundModal.fragment}

  fragment RefundPayments on Payment {
    id
    refunds {
      id
      timestamp
      name
      description
      amount
      ...DeleteRefundModal
    }
  }
`;

export default RefundPayments;
